import {
  TETO_CREDITOS_MINUTOS,
  relataHoras,
  type Mes,
  type Perfil,
  type SemanaDoMes,
} from '@sheafyield/dominio';
import { gravar, idDoPerfil, type Banco } from '@sheafyield/sync';

import type { DonoDasLinhas } from '../dados/dono.js';
import {
  entradaDoMes,
  mesmoConteudo,
  retratoDentroDaTransacao,
  validarAgora,
  type RetratoDoRelatorio,
} from './leituraDoRelatorio.js';

export { lerRelatorio } from './leituraDoRelatorio.js';

export interface VisaoDoRelatorio {
  perfil: Perfil;
  perfilPersistido: boolean;
  atual: Mes;
  automatico: Mes;
  meses: Mes[];
  semanas: SemanaDoMes[];
  baseCompleta: boolean;
  revisao: string;
}

export interface EdicaoDoMes {
  participou: boolean;
  estudos: number;
  horas_minutos: number;
  creditos_minutos: number;
  observacao: string;
}

const MENSAGEM_DE_ALTERACAO =
  'Os dados deste mês mudaram. Confira os valores atualizados antes de salvar.';

export class RelatorioAlterado extends Error {
  override readonly name = 'RelatorioAlterado';

  constructor() {
    super(MENSAGEM_DE_ALTERACAO);
  }
}

async function donoEstavel(dono: DonoDasLinhas): Promise<string> {
  // `auth.getSession()` é uma promessa externa ao IndexedDB. As duas
  // leituras ficam antes da transação para ela não cometer enquanto espera;
  // a segunda fecha a janela entre a intenção e a abertura da escrita.
  const primeiro = await dono();
  const confirmado = await dono();
  if (primeiro !== confirmado) throw new RelatorioAlterado();
  return confirmado;
}

export async function salvarPerfil(
  banco: Banco,
  dono: DonoDasLinhas,
  entrada: Pick<Perfil, 'tipo' | 'meta_horas_mes' | 'nome_exibicao'>,
): Promise<Perfil> {
  const user_id = await donoEstavel(dono);
  return banco.transaction('rw', banco.perfil, banco.fila, async () => {
    const id = idDoPerfil(user_id);
    const atual = await banco.perfil.get(id);
    if (
      atual !== undefined &&
      !atual.deleted &&
      atual.user_id === user_id &&
      atual.tipo === entrada.tipo &&
      atual.meta_horas_mes === entrada.meta_horas_mes &&
      atual.nome_exibicao === entrada.nome_exibicao
    )
      return atual;
    return gravar(banco, 'perfil', {
      id,
      user_id,
      ...entrada,
    });
  });
}

function validarEdicao(entrada: EdicaoDoMes): void {
  if (typeof entrada.participou !== 'boolean')
    throw new TypeError('A participação precisa ser informada.');
  if (!Number.isInteger(entrada.estudos) || entrada.estudos < 0)
    throw new RangeError('Estudos deve ser um inteiro não negativo.');
  if (
    !Number.isInteger(entrada.horas_minutos) ||
    entrada.horas_minutos < 0 ||
    entrada.horas_minutos % 60 !== 0
  )
    throw new RangeError('Horas deve ser um múltiplo não negativo de 60.');
  if (
    !Number.isInteger(entrada.creditos_minutos) ||
    entrada.creditos_minutos < 0 ||
    entrada.creditos_minutos > TETO_CREDITOS_MINUTOS
  )
    throw new RangeError('Créditos deve ficar entre zero e 55 horas.');
  if (typeof entrada.observacao !== 'string')
    throw new TypeError('A observação precisa ser informada.');
}

async function comRetratoParaEscrita<T>(
  banco: Banco,
  dono: DonoDasLinhas,
  ano: number,
  mes: number,
  revisao: string,
  agora: Date,
  executar: (retrato: RetratoDoRelatorio, dono: string) => Promise<T>,
): Promise<T> {
  validarAgora(agora);
  const user_id = await donoEstavel(dono);
  return banco.transaction(
    'rw',
    [
      banco.perfil,
      banco.sessao,
      banco.estudo,
      banco.visita,
      banco.mes,
      banco.estado,
      banco.fila,
    ],
    async () => {
      const retrato = await retratoDentroDaTransacao(
        banco,
        user_id,
        ano,
        mes,
        agora,
      );
      if (retrato.visao.revisao !== revisao) throw new RelatorioAlterado();
      return executar(retrato, user_id);
    },
  );
}

export async function editarMes(
  banco: Banco,
  dono: DonoDasLinhas,
  ano: number,
  mes: number,
  entrada: EdicaoDoMes,
  revisao: string,
  agora: Date,
): Promise<Mes> {
  validarEdicao(entrada);
  return await comRetratoParaEscrita(
    banco,
    dono,
    ano,
    mes,
    revisao,
    agora,
    async (retrato) => {
      if (!retrato.visao.baseCompleta && retrato.alvoPersistido === undefined)
        throw new Error(
          'A edição manual requer um mês persistido enquanto a base completa ainda não chegou.',
        );
      const atual = retrato.alvoPersistido ?? retrato.visao.atual;
      const mostraHoras = relataHoras(retrato.visao.perfil.tipo);
      const candidata: Mes = {
        ...atual,
        participou: entrada.participou,
        estudos: entrada.estudos,
        horas_minutos: mostraHoras
          ? entrada.horas_minutos
          : atual.horas_minutos,
        creditos_minutos: mostraHoras
          ? entrada.creditos_minutos
          : atual.creditos_minutos,
        observacao: entrada.observacao,
        transporte_minutos: atual.transporte_minutos,
        sobrescrito: true,
      };
      if (
        retrato.alvoPersistido &&
        mesmoConteudo(retrato.alvoPersistido, candidata)
      )
        return retrato.alvoPersistido;
      return gravar(banco, 'mes', entradaDoMes(candidata), agora.toISOString());
    },
  );
}

export function retomarAutomatico(
  banco: Banco,
  dono: DonoDasLinhas,
  ano: number,
  mes: number,
  revisao: string,
  agora: Date,
): Promise<Mes> {
  return comRetratoParaEscrita(
    banco,
    dono,
    ano,
    mes,
    revisao,
    agora,
    async (retrato) => {
      if (!retrato.visao.baseCompleta)
        throw new Error(
          'O retorno automático exige a base completa do relatório.',
        );
      let alvo: Mes | undefined;
      for (const calculado of retrato.serieAutomatica) {
        const persistido = retrato.mesesPersistidos.find(
          (item) => item.ano === calculado.ano && item.mes === calculado.mes,
        );
        const eAlvo = calculado.ano === ano && calculado.mes === mes;
        if (!eAlvo && persistido === undefined) continue;
        if (persistido && mesmoConteudo(persistido, calculado)) {
          if (eAlvo) alvo = persistido;
          continue;
        }
        const salvo = await gravar(
          banco,
          'mes',
          entradaDoMes(calculado),
          agora.toISOString(),
        );
        if (eAlvo) alvo = salvo;
      }
      if (alvo === undefined)
        throw new Error('O mês automático solicitado não foi encontrado.');
      return alvo;
    },
  );
}

export function marcarEnviado(
  banco: Banco,
  dono: DonoDasLinhas,
  ano: number,
  mes: number,
  revisao: string,
  agora: Date,
): Promise<Mes> {
  return comRetratoParaEscrita(
    banco,
    dono,
    ano,
    mes,
    revisao,
    agora,
    async (retrato) => {
      if (!retrato.visao.baseCompleta)
        throw new Error(
          'Marcar como enviado exige a base completa do relatório.',
        );
      if (retrato.alvoPersistido?.enviado_em) return retrato.alvoPersistido;
      const candidata = {
        ...retrato.visao.atual,
        enviado_em: agora.toISOString(),
      };
      return gravar(banco, 'mes', entradaDoMes(candidata), agora.toISOString());
    },
  );
}
